import React, { useState, useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import { LoadingSpinner } from '../ui/Icons';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Send } from 'lucide-react';

const isDateSame = (d1, d2) => {
    const a = new Date(d1), b = new Date(d2);
    return a.toDateString() === b.toDateString();
};

const formatDateSeparator = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === now.toDateString()) return 'Heute';
    if (date.toDateString() === yesterday.toDateString()) return 'Gestern';
    return date.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });
};

const ChatWindow = ({
    activeConversation, messages, currentUser,
    onSendMessage, loadingMessages, onBack,
    onAcceptDuel, onDeclineDuel, onInviteDuel
}) => {
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, activeConversation]);

    useEffect(() => {
        if (activeConversation) inputRef.current?.focus();
    }, [activeConversation]);

    const handleSend = (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        onSendMessage(newMessage);
        setNewMessage('');
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) handleSend(e);
    };

    if (!activeConversation) return null;
    const partner = activeConversation.partner;

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-[#0d0d0d]">

            {/* ── Header ── */}
            <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 bg-white dark:bg-[#141414] border-b border-gray-100 dark:border-white/5 shadow-sm">
                {/* Back button (mobile) */}
                <button
                    onClick={onBack}
                    className="md:hidden w-9 h-9 -ml-1 flex items-center justify-center rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 text-gray-600 dark:text-gray-300 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>

                {/* Avatar + Info */}
                <button
                    className="flex items-center gap-3 flex-1 min-w-0 group text-left"
                    onClick={() => navigate(`/user/@${partner.username}`)}
                >
                    <div className="relative flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-plantlix-500 to-plantlix-700 flex items-center justify-center text-white font-black shadow-sm">
                            {partner.username.charAt(0).toUpperCase()}
                        </div>
                        {partner.is_online && (
                            <span className="absolute bottom-0 right-0 block w-2.5 h-2.5 bg-plantlix-500 rounded-full border-2 border-white dark:border-[#141414]" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className="font-bold text-sm text-gray-900 dark:text-white truncate group-hover:text-plantlix-600 dark:group-hover:text-plantlix-400 transition-colors">
                            {partner.username}
                        </p>
                        <p className={`text-[11px] font-semibold ${partner.is_online ? 'text-plantlix-500' : 'text-gray-400'}`}>
                            {partner.is_online ? '● Online' : '○ Offline'}
                        </p>
                    </div>
                </button>

                {/* Actions */}
                <div className="flex items-center gap-1">
                    {onInviteDuel && (
                        <button
                            onClick={onInviteDuel}
                            className="w-9 h-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all"
                            title="Zum Duell herausfordern"
                        >
                            <Trophy className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Messages ── */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                {loadingMessages ? (
                    <div className="flex justify-center items-center h-full">
                        <LoadingSpinner />
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center opacity-60">
                        <div className="text-5xl mb-3">👋</div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">Sag Hallo!</p>
                    </div>
                ) : (
                    messages.map((msg, index) => {
                        const isOwn = msg.sender === currentUser.id;
                        const prevMsg = messages[index - 1];
                        const showDateSep = !prevMsg || !isDateSame(prevMsg.timestamp, msg.timestamp);
                        return (
                            <React.Fragment key={msg.id || `temp-${index}`}>
                                {showDateSep && (
                                    <div className="flex items-center justify-center my-4">
                                        <span className="text-[11px] font-bold text-gray-400 dark:text-gray-600 uppercase tracking-wider bg-gray-100 dark:bg-white/5 px-3 py-1 rounded-full">
                                            {formatDateSeparator(msg.timestamp)}
                                        </span>
                                    </div>
                                )}
                                <MessageBubble
                                    message={msg}
                                    isOwn={isOwn}
                                    onAcceptDuel={onAcceptDuel}
                                    onDeclineDuel={onDeclineDuel}
                                />
                            </React.Fragment>
                        );
                    })
                )}
                <div ref={messagesEndRef} className="h-2" />
            </div>

            {/* ── Input ── */}
            <div className="flex-shrink-0 px-4 py-3 bg-white dark:bg-[#141414] border-t border-gray-100 dark:border-white/5">
                <form onSubmit={handleSend} className="flex items-end gap-2">
                    <div className="flex-1 relative">
                        <textarea
                            ref={inputRef}
                            rows={1}
                            value={newMessage}
                            onChange={(e) => {
                                setNewMessage(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="Nachricht schreiben..."
                            className="w-full px-4 py-3 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:ring-2 focus:ring-plantlix-500/50 focus:border-plantlix-500 outline-none transition-all resize-none text-sm leading-relaxed"
                            style={{ minHeight: '44px', maxHeight: '120px' }}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-2xl bg-plantlix-500 hover:bg-plantlix-600 disabled:opacity-40 disabled:cursor-not-allowed text-white shadow-lg shadow-plantlix-500/25 transition-all hover:scale-105 active:scale-95"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </form>
                <p className="text-[10px] text-gray-400 mt-1.5 ml-1">Enter zum Senden · Shift+Enter für neue Zeile</p>
            </div>
        </div>
    );
};

export default ChatWindow;
